import { ENDPOINTS } from '../config/constants';
import { SamedayCounty, SamedayCity } from '../types/nomenclator';

class NomenclatorCache {
  private static instance: NomenclatorCache;
  private cache: Map<string, any> = new Map();
  private fetchPromises: Map<string, Promise<any>> = new Map();
  private lastUpdate: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 6 * 60 * 60 * 1000; // 6 hours

  private constructor() {}

  static getInstance(): NomenclatorCache {
    if (!this.instance) {
      this.instance = new NomenclatorCache();
    }
    return this.instance;
  }

  private needsRefresh(key: string): boolean {
    const lastUpdate = this.lastUpdate.get(key);
    if (!lastUpdate) return true;
    return Date.now() - lastUpdate > this.CACHE_DURATION;
  }

  async getData<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
    // Return cached data if valid
    if (this.cache.has(key) && !this.needsRefresh(key)) {
      return this.cache.get(key);
    }

    // Return existing fetch promise if one is in progress
    let existingPromise = this.fetchPromises.get(key);
    if (existingPromise) {
      return existingPromise;
    }

    // Create new fetch promise
    try {
      const promise = fetchFn();
      this.fetchPromises.set(key, promise);

      const data = await promise;
      this.cache.set(key, data);
      this.lastUpdate.set(key, Date.now());
      return data;
    } finally {
      // Clean up the fetch promise regardless of success/failure
      this.fetchPromises.delete(key);
    }
  }

  clearCache() {
    this.cache.clear();
    this.lastUpdate.clear();
    this.fetchPromises.clear();
  }
}

export class NomenclatorService {
  private static instance: NomenclatorService;
  private cache = NomenclatorCache.getInstance();
  private readonly MAX_RETRY_ATTEMPTS = 2;
  private readonly BASE_RETRY_DELAY = 1000; // 1 second

  private constructor() {}

  static getInstance(): NomenclatorService {
    if (!this.instance) {
      this.instance = new NomenclatorService();
    }
    return this.instance;
  }

  private async fetchWithRetry<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> {
    let attempt = 0;
    let lastError: Error | null = null;

    while (attempt < this.MAX_RETRY_ATTEMPTS) {
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        attempt++;
        
        if (attempt < this.MAX_RETRY_ATTEMPTS) {
          await new Promise(resolve => setTimeout(resolve, this.BASE_RETRY_DELAY * attempt));
        }
      }
    }

    throw lastError || new Error('Failed to fetch after retries');
  }

  async getCounties(): Promise<SamedayCounty[]> {
    return this.cache.getData<SamedayCounty[]>('counties', async () => {
      const response = await this.fetchWithRetry<{ data: SamedayCounty[] }>(ENDPOINTS.NOMENCLATOR.JUDETE);
      return response.data;
    });
  }

  async getCitiesForCounty(countyId?: number): Promise<SamedayCity[]> {
    const cacheKey = countyId ? `cities_${countyId}` : 'all_cities';
    return this.cache.getData<SamedayCity[]>(cacheKey, async () => {
      const response = await this.fetchWithRetry<{ data: SamedayCity[] }>(ENDPOINTS.NOMENCLATOR.LOCALITATI);
      const allCities = response.data;
      
      // If countyId is provided, filter cities for that county
      if (countyId) {
        return allCities.filter(city => city.county === countyId);
      }
      
      return allCities;
    });
  }

  async refreshCache(): Promise<void> {
    this.cache.clearCache();
    await this.getCounties();
  }
}

export const nomenclatorService = NomenclatorService.getInstance();
